import { dayjs } from '@noor/lib';
import type { DateRangeValue } from '@noor/ui';

export const DAY_PARAM_FORMAT = 'YYYY-MM-DD';
const DAY_LABEL_FORMAT = 'D.MM.YYYY';

interface RangePreset {
  label: string;
  getRange: () => DateRangeValue;
}

/** URL day bounds (`YYYY-MM-DD`) → picker range. Lenient: invalid reads as unset. */
export function toDateRangeValue(
  dateFrom: string | undefined,
  dateTo: string | undefined,
): DateRangeValue | undefined {
  if (!dateFrom) return undefined;
  const from = dayjs(dateFrom, DAY_PARAM_FORMAT, true);
  if (!from.isValid()) return undefined;
  const to = dateTo ? dayjs(dateTo, DAY_PARAM_FORMAT, true) : undefined;
  return {
    from: from.toDate(),
    ...(to?.isValid() ? { to: to.toDate() } : {}),
  };
}

/** Picker range → URL day bounds. Single-day selections keep `dateTo` unset. */
export function toDateParams(range: DateRangeValue): {
  dateFrom: string;
  dateTo: string | undefined;
} {
  return {
    dateFrom: dayjs(range.from).format(DAY_PARAM_FORMAT),
    dateTo:
      range.to !== undefined && !dayjs(range.from).isSame(range.to, 'day')
        ? dayjs(range.to).format(DAY_PARAM_FORMAT)
        : undefined,
  };
}

/** Pill caption: `Preset: 1.09.2026 - 7.09.2026`, a bare range, or the placeholder. */
export function formatDatePillLabel(
  range: DateRangeValue | undefined,
  presets: RangePreset[],
  placeholder: string,
): string {
  if (!range) return placeholder;
  const match = presets.find((preset) => isSameRange(preset.getRange(), range));
  const rangeText =
    range.to !== undefined && !dayjs(range.from).isSame(range.to, 'day')
      ? `${dayjs(range.from).format(DAY_LABEL_FORMAT)} - ${dayjs(range.to).format(DAY_LABEL_FORMAT)}`
      : dayjs(range.from).format(DAY_LABEL_FORMAT);
  return match ? `${match.label}: ${rangeText}` : rangeText;
}

function isSameRange(a: DateRangeValue, b: DateRangeValue): boolean {
  return (
    dayjs(a.from).isSame(b.from, 'day') &&
    (a.to === undefined) === (b.to === undefined) &&
    (a.to === undefined || b.to === undefined || dayjs(a.to).isSame(b.to, 'day'))
  );
}
